import { Badge, Group, Paper, Radio, Stack, Text } from "@mantine/core";
import { MultiSelect } from "react-hook-form-mantine";

const tickets = (n) => (n === 1 ? "1 ticket" : `${n} tickets`);

// Who the audience reaches and who it leaves out, from the recipient count:
// holders in the app at their account's email; tickets not in the app yet at
// the ticket's email, only when the campaign carries the ticket's code or
// link; never tickets without an email.
export const audienceLines = (audience) => {
  if (!audience) return { included: [], skipped: [] };
  const {
    inApp = 0,
    notInApp = 0,
    notInAppIncluded = false,
    withoutEmail = 0,
  } = audience;
  const included = [];
  const skipped = [];
  if (inApp > 0) {
    included.push(
      `${tickets(inApp)} in the app, at the email of the account that holds it`
    );
  }
  if (notInApp > 0) {
    if (notInAppIncluded) {
      included.push(
        `${tickets(notInApp)} not in the app yet, at the ticket's email, each with its own code`
      );
    } else {
      skipped.push(
        `${tickets(notInApp)} not in the app yet: add Ticket Code or Ticket Link to the email to reach them`
      );
    }
  }
  if (withoutEmail > 0) {
    skipped.push(`${tickets(withoutEmail)} without an email`);
  }
  return { included, skipped };
};

// Audience selector for a campaign: either all attendees or a tag-filtered
// subset. The resolved recipient count is shown in the header badge, and
// below it who is included and who is skipped.
const TargetAudience = ({
  control,
  audienceType,
  onAudienceTypeChange,
  tags,
  totalRecipients,
  audience,
}) => {
  const { included, skipped } = audienceLines(audience);
  return (
    <Paper withBorder shadow="md" p="md" radius="md">
      <Group justify="space-between" align="center" mb="sm">
        <Text fw={500}>Target Audience</Text>
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Recipients:
          </Text>
          <Badge variant="light" size="lg">
            {totalRecipients}
          </Badge>
        </Group>
      </Group>
      <Stack gap="sm">
        <Radio.Group value={audienceType} onChange={onAudienceTypeChange}>
          <Group>
            <Radio value="all" label="All attendees" />
            <Radio value="tags" label="Select by tags" />
          </Group>
        </Radio.Group>

        {audienceType === "tags" && (
          <MultiSelect
            name="targetedTagIds"
            control={control}
            data={
              tags?.map((t) => ({
                value: String(t.id),
                label: `${t.title} (${t.usageCount})`,
              })) || []
            }
            placeholder="Select tags"
            searchable
          />
        )}

        {included.length > 0 && (
          <Stack gap={2} data-testid="audience-included">
            <Text size="sm" fw={500}>
              Included
            </Text>
            {included.map((line) => (
              <Text key={line} size="sm" c="dimmed">
                {line}
              </Text>
            ))}
          </Stack>
        )}
        {skipped.length > 0 && (
          <Stack gap={2} data-testid="audience-skipped">
            <Text size="sm" fw={500}>
              Skipped
            </Text>
            {skipped.map((line) => (
              <Text key={line} size="sm" c="dimmed">
                {line}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default TargetAudience;
